import { useEffect, useRef, useCallback } from 'react'

import * as Y from 'yjs'
import { WebsocketProvider } from 'y-websocket'
import { Awareness } from 'y-protocols/awareness'
import { throttle } from 'lodash'

import { fromYMapToJS } from '../utils/yjsTransfrom.js'
import { addPath, updatePath, updateTranslateAndScope } from '../utils/yjsActions.js'

// [store]
import { useWhiteboardStore } from '../stores/whiteboardStore.js'
import { useUIStore } from '../stores/uiStore.js'

/**
 * Yjs 웹소켓 연결, 문서 초기화, 데이터 구독을 관리하는 커스텀 훅
 * @param {object} params
 * @param {string} params.wsUrl - 웹소켓 서버 URL
 * @param {string} params.boardId - 동기화를 위한 룸 ID
 * @param {string} params.boardToken - 인증 토큰
 * @param {boolean} params.isListener - 리스너 모드 여부 (scope/translate 구독 결정)
 * @param {boolean} params.isLeader
 * @param {React.MutableRefObject<number>} params.scaleRatio - 화면 비율 Ref
 * @param {function} params.onPathsUpdate - paths 데이터가 업데이트될 때 호출될 콜백 함수
 * @param {function} params.onScopeTranslateUpdate - scope/translate 데이터가 업데이트될 때 호출될 콜백 함수
 */

export const useYjsService = ({
  wsUrl,
  boardId,
  boardToken,
  isListener,
  isLeader,
  scaleRatio,
  onPathsUpdate,
  onScopeTranslateUpdate,
}) => {
  const { setYjsRefs, setYjsActions, resetYjsData } = useWhiteboardStore()
  const setLoading = useUIStore((state) => state.setLoading)

  // Y.Doc은 앱 라이프사이클 동안 하나만 생성
  /*   const ydocRef = useRef(null)
  if (!ydocRef.current) {
    ydocRef.current = new Y.Doc()
  } */

  // Yjs core 객체Ref
  const ydocRef = useRef(null)
  const providerRef = useRef(null)
  const yPathsRef = useRef(null)
  const yScopeRef = useRef(null)
  const yTranslateRef = useRef(null)
  const isProviderSyncedRef = useRef(false)

  useEffect(() => {
    if (!boardToken) return

    /**
     * 1. Y.Doc & Provider 초기화
     */

    // Yjs 문서 객체, 공유 상태를 담는 문서
    const ydoc = new Y.Doc()

    // 클라이언트가 WebsocketProvider를 통해 Yjs 서버에 접속
    // boardId를 방 이름으로 하여, 같은 방에 있는 사용자끼리 동일한 ydoc 상태를 공유
    const provider = new WebsocketProvider(wsUrl, boardId, ydoc, {
      awareness: new Awareness(ydoc),
      // params: { token: boardToken },
    })
    providerRef.current = provider

    /**
     * 2. 이벤트 리스너 설정
     */

    // [ydoc] 문서(ydoc)에 변경 사항이 적용되었을 때 트리거: 로컬 or 리모트 모두 포함해서 문서가 변경되었을 때 호출
    ydoc.on('update', () => {
      console.log('update')
    })

    // [provider] Yjs 프로바이더의 네트워크 연결 상태가 바뀔 때 트리거
    provider.on('status', (event) => {
      // connected → WebSocket/WebRTC가 정상 연결된 상태
      if (isProviderSyncedRef.current && event.status === 'connected') setLoading(false)

      // disconnected → 네트워크가 끊어졌거나 서버 연결 실패
      if (event.status === 'disconnected') setLoading(true)
      console.log('[YJS STATUS]', event.status) // logs "connected" or "disconnected"
    })

    // 토큰 만료 등으로 인한 연결 종료 시 새로고침
    // 💫우주: 지구과학 수능 문제랑 지구과학 ppt 넣고 선 그리면 깨짐.
    provider.on('connection-close', (event) => {
      if (!event) {
        return
      }
      console.error('[YJS CLOSE]', 'code:', event.code, 'reason:', event.reason)
      if (event.code === 1008 && event.reason === 'Unauthorized') {
        // 우선 임시 새로고침
        window.location.reload()
      }
    })

    // 최초 연결 후, 문서와의 동기화가 완료되었을 때 호출
    provider.once('sync', (isSynced) => {
      if (isSynced) {
        console.log('synced')
        isProviderSyncedRef.current = true
        setLoading(false)
      }
    })

    /**
     * 3. Yjs 공유 데이터 타입 정의
     */

    const yPaths = ydoc.getMap('paths') // 그리기 객체들
    const yScope = ydoc.getMap('scopes') // 확대/축소 비율
    const yTranslate = ydoc.getMap('translate') // 위치 이동 값

    ydocRef.current = ydoc
    yPathsRef.current = yPaths
    yScopeRef.current = yScope
    yTranslateRef.current = yTranslate

    /**
     * 4. 데이터 변경 감지 및 콜백 호출
     */

    // Path 데이터 업데이트 핸들러 (throttle 적용)
    const handlePathsUpdate = throttle(() => {
      if (!yPaths) return

      const result = Object.fromEntries(
        Array.from(yPaths.entries()).map(([key, yMap]) => {
          return [key, fromYMapToJS(yMap)]
        })
      )
      onPathsUpdate(result)
    }, 10)
    yPaths.observeDeep(handlePathsUpdate) // yPaths 내부의 Y.Map, Y.Array를 포함한 모든 중첩 구조의 변경을 감지
    handlePathsUpdate() // 초기 데이터 로드

    // Scope & Translate 업데이트 핸들러 (throttle 적용)
    const handleScopeTranslateUpdate = () => {
      if (!yScope || !yTranslate || !ydocRef.current) return

      const scopeEntries = Object.fromEntries(yScope.entries())
      const translateEntries = Object.fromEntries(yTranslate.entries())

      // 본인의 수정이면 무시
      if (scopeEntries['ydocId']) {
        if (scopeEntries['ydocId'] === ydocRef.current.clientID) return
      }
      onScopeTranslateUpdate(scopeEntries, translateEntries)
    }

    // 리스너 모드일 때만 scope/translate 변경을 구독
    if (isListener) {
      yScope.observe(handleScopeTranslateUpdate)
      yTranslate.observe(handleScopeTranslateUpdate)
      handleScopeTranslateUpdate() // 초기 데이터 로드
    }

    // 컴포넌트 언마운트 시 클린업 함수
    return () => {
      console.log('[YJS] Cleaning up connection...')
      yPaths.unobserveDeep(handlePathsUpdate)
      handlePathsUpdate.cancel()
      if (isListener) {
        yScope.unobserve(handleScopeTranslateUpdate)
        yTranslate.unobserve(handleScopeTranslateUpdate)
      }
      provider.disconnect()
      provider.destroy()
      ydoc.destroy()
      resetYjsData()
    }
  }, [
    boardId,
    boardToken,
    wsUrl,
    isListener,
    onPathsUpdate,
    onScopeTranslateUpdate,
    resetYjsData,
    setLoading,
  ])

  /**
   * 5. Yjs Trigger 함수 생성
   */

  const triggerAddPath = useCallback(
    (pathId, pathObj) => {
      if (!ydocRef.current || !yPathsRef.current) return
      addPath(ydocRef.current, yPathsRef.current, pathId, pathObj)
    },
    [ydocRef, yPathsRef]
  )

  const triggerUpdatePath = useCallback(
    (pathId, updates, pointUpdateType, appendPointObj) => {
      if (!ydocRef.current || !yPathsRef.current) return
      updatePath(
        ydocRef.current,
        yPathsRef.current,
        pathId,
        updates,
        pointUpdateType,
        appendPointObj
      )
    },
    [ydocRef, yPathsRef]
  )

  const triggerUpdateTranslateAndScope = useCallback(
    (newTranslate, newScope) => {
      if (!isLeader || !ydocRef.current || !yTranslateRef.current || !yScopeRef.current) return

      updateTranslateAndScope(
        ydocRef.current,
        yTranslateRef.current,
        yScopeRef.current,
        newTranslate,
        newScope,
        scaleRatio.current
      )
    },
    [isLeader, ydocRef, yTranslateRef, yScopeRef, scaleRatio]
  )

  /**
   * 6. 생성된 모든 객체와 함수를 스토어에 저장
   */

  useEffect(() => {
    setYjsRefs({
      ydocRef,
      yPathsRef,
      yScopeRef,
      yTranslateRef,
    })

    setYjsActions({
      triggerAddPath,
      triggerUpdatePath,
      triggerUpdateTranslateAndScope,
    })
  }, [setYjsRefs, setYjsActions, triggerAddPath, triggerUpdatePath, triggerUpdateTranslateAndScope])

  return {
    providerRef,
    ydocRef,
    yPathsRef,
    yScopeRef,
    yTranslateRef,
    triggerAddPath,
    triggerUpdatePath,
    triggerUpdateTranslateAndScope,
  }
}
